/**
 * @file
 * ---------------------------------------------------------------------------
 * RAOC-23 migration shim — `*FromRovoConfig` hooks
 * ---------------------------------------------------------------------------
 * Co-located file for the RAOC-23 migration shim. Pairs with `./index.ts`,
 * which owns the legacy `react-sweet-state` store + `*FromKnowledge` hooks
 * and exports the public `useX` wrappers that branch on the
 * `rovo_cas_use_rovo_config` feature gate.
 *
 * Everything in this file reads from `useRovoConfigStore` in
 * `@atlassian/rovo-connectors-config`. Nothing here touches the legacy
 * store.
 *
 * Tenant threading (`siteId` === cloudId): connector data on this path is
 * fetched **declaratively** by `useRovoConfigStore({ cloudId })`, so the
 * tenant must be supplied to that hook. The tenant only enters the system via
 * the imperative `fetchIfEmpty(fetcher, siteId)` / `prefetchIfEmpty(fetcher,
 * siteId, …)` action calls (in `useEnsureConnectorsLoaded` and
 * `WarmChatFetches`). Those actions are no-ops for *writing* on this path, but
 * they now **capture** the `siteId` into the co-located reactive store in
 * `./rovo-config-site-id-store.ts`; every selector hook reads that captured
 * `siteId` back and forwards it to `useRovoConfigStore({ cloudId })`. Until a
 * `siteId` has been captured, `cloudId` is `undefined` and the rovo-config
 * store stays safely idle.
 *
 * TODO RAOC-23: once `rovo_cas_use_rovo_config` is fully rolled out, inline
 *               the body of each `*FromRovoConfig` hook back into its public
 *               `useX` wrapper in `./index.ts`, delete the matching
 *               `*FromKnowledge` legacy hooks + the `react-sweet-state`
 *               store, and delete this file (and the siteId-store helper).
 * ---------------------------------------------------------------------------
 */

import { useMemo } from 'react';

import { useRovoConfigStore } from '@atlassian/rovo-connectors-config/useRovoConfigStore';

import { useCaptureRovoConfigSiteId, useRovoConfigSiteId } from './rovo-config-site-id-store';

import type { BoundConnectorsStoreActions, ConnectorState, ConnectorsStoreState } from './index';
import { isNewlyEnabledApp } from './index';

const warnNoop =
	(name: string) =>
	(...args: unknown[]) => {
		// eslint-disable-next-line no-console
		console.warn(
			`[RAOC-23] No-op stub called: ${name}. Under rovo_cas_use_rovo_config FG, connectors-store actions are not yet wired.`,
			...args,
		);
	};

/**
 * No-op action stubs for the connectors-store *writes* that are not yet wired
 * on the rovo-config path.
 *
 * TODO RAOC-23: implement the remaining real actions backed by
 *               `useRovoConfigStore` (setKnowledgeSources, setError,
 *               markLastUsed)
 */
const NOOP_WRITE_ACTIONS: Pick<
	BoundConnectorsStoreActions,
	'setKnowledgeSources' | 'setError' | 'markLastUsed'
> = {
	setKnowledgeSources: warnNoop('setKnowledgeSources'),
	setError: warnNoop('setError'),
	markLastUsed: warnNoop('markLastUsed'),
};

/**
 * Single co-located controller for the rovo-config FG path.
 *
 * Consolidates:
 *
 *   1. the captured tenant (`siteId` === cloudId) read back from
 *      {@link useRovoConfigSiteId};
 *   2. the connector read-state, fetched declaratively by
 *      `useRovoConfigStore({ cloudId })`; and
 *   3. the bound actions object (tenant-capturing `fetchIfEmpty` /
 *      `prefetchIfEmpty`, the real `markUserConnected`, and the remaining
 *      no-op writes).
 *
 * Every `*FromRovoConfig` selector hook delegates here and then projects the
 * slice of state it needs.
 */
const useRovoConfigConnectorsController = (): [
	ConnectorsStoreState,
	BoundConnectorsStoreActions,
] => {
	const captureSiteId = useCaptureRovoConfigSiteId();
	const siteId = useRovoConfigSiteId();
	const { markUserConnected, ...state } = useRovoConfigStore({ cloudId: siteId });

	const actions = useMemo<BoundConnectorsStoreActions>(
		() => ({
			...NOOP_WRITE_ACTIONS,
			markUserConnected,
			fetchIfEmpty: (async (_fetcher: () => Promise<unknown>, siteId: string): Promise<void> => {
				// Capture the tenant so this controller can drive
				// `useRovoConfigStore({ cloudId: siteId })`.
				captureSiteId(siteId);
			}) as never,
			prefetchIfEmpty: (async (
				_fetcher: () => Promise<unknown>,
				siteId: string,
				callbacks?: {
					onStart?: () => void;
					onSuccess?: () => void;
					onError?: (error: Error) => void;
				},
			): Promise<void> => {
				callbacks?.onStart?.();
				// Capture the tenant so this controller can drive
				// `useRovoConfigStore({ cloudId: siteId })`. The actual fetch
				// lifecycle (success/error) is owned by the declarative
				// `useRovoConfig` fetch, not by this stub.
				captureSiteId(siteId);
			}) as never,
		}),
		[captureSiteId, markUserConnected],
	);

	return [state as ConnectorsStoreState, actions];
};

/**
 * @internal Consumers must use `useKnowledgeSourcesStore` directly.
 */
export const useKnowledgeSourcesStoreFromRovoConfig = (): [
	ConnectorsStoreState,
	BoundConnectorsStoreActions,
] => useRovoConfigConnectorsController();

/**
 * @internal Consumers must use `useKnowledgeSourcesActions` directly.
 */
export const useKnowledgeSourcesActionsFromRovoConfig = (): BoundConnectorsStoreActions => {
	const [, actions] = useRovoConfigConnectorsController();
	return actions;
};

/**
 * @internal Consumers must use `useConnectors` directly.
 */
export const useConnectorsFromRovoConfig = (): [
	Record<string, ConnectorState>,
	BoundConnectorsStoreActions,
] => {
	const [state, actions] = useRovoConfigConnectorsController();
	return [state.connectors as Record<string, ConnectorState>, actions];
};

/**
 * @internal Consumers must use `useEnabledKnowledgeSources` directly.
 */
export const useEnabledKnowledgeSourcesFromRovoConfig = (): [
	ConnectorState[],
	BoundConnectorsStoreActions,
] => {
	const [state, actions] = useRovoConfigConnectorsController();
	return [Object.values(state.connectors) as ConnectorState[], actions];
};

/**
 * @internal Consumers must use `useConnectorsIsFetching` directly.
 */
export const useConnectorsIsFetchingFromRovoConfig = (): [boolean, BoundConnectorsStoreActions] => {
	const [state, actions] = useRovoConfigConnectorsController();
	return [state.isFetching, actions];
};

/**
 * @internal Consumers must use `useConnectorsDataReady` directly.
 */
export const useConnectorsDataReadyFromRovoConfig = (): [boolean, BoundConnectorsStoreActions] => {
	const [state, actions] = useRovoConfigConnectorsController();
	return [state.hasFetchAttempted && !state.isFetching, actions];
};

/**
 * @internal Consumers must use `useNewlyEnabledConnectors` directly.
 */
export const useNewlyEnabledConnectorsFromRovoConfig = (): ConnectorState[] => {
	const [state] = useRovoConfigConnectorsController();
	return (Object.values(state.connectors) as ConnectorState[]).filter(isNewlyEnabledApp);
};

/**
 * @internal Consumers must use `useConnectorNudgeActions` directly.
 */
export const useConnectorNudgeActionsFromRovoConfig = (): Pick<
	BoundConnectorsStoreActions,
	'markUserConnected'
> => {
	const [, { markUserConnected }] = useRovoConfigConnectorsController();
	return useMemo(() => ({ markUserConnected }), [markUserConnected]);
};
